import { useEffect, useRef, useState } from 'preact/hooks';
import { useOddball } from '../midi-context';
import { getAudioContext } from '../audio/audio-engine';
import { useCCValues } from '../hooks/use-oddball-events';
import type { TapEvent, TwistEvent, CCUpdateEvent } from '../../src/types';
import { OddballCC } from '../../src/types';

interface PadEngine {
  ctx: AudioContext;
  osc: OscillatorNode;
  filter: BiquadFilterNode;
  distortion: WaveShaperNode;
  distortionGain: GainNode;
  masterGain: GainNode;
  fmOsc: OscillatorNode;
  fmGain: GainNode;
  octaveShift: number;
}

function makeDistortionCurve(amount: number): Float32Array {
  const samples = 256;
  const curve = new Float32Array(samples);
  for (let i = 0; i < samples; i++) {
    const x = (i * 2) / samples - 1;
    curve[i] = ((3 + amount) * x * 20 * (Math.PI / 180)) / (Math.PI + amount * Math.abs(x));
  }
  return curve;
}

function createPadEngine(): PadEngine {
  const ctx = getAudioContext();

  const osc = ctx.createOscillator();
  osc.type = 'sawtooth';
  osc.frequency.value = 220;

  const filter = ctx.createBiquadFilter();
  filter.type = 'lowpass';
  filter.frequency.value = 1000;
  filter.Q.value = 3;

  const distortion = ctx.createWaveShaper();
  distortion.curve = makeDistortionCurve(0);
  distortion.oversample = '2x';

  const distortionGain = ctx.createGain();
  distortionGain.gain.value = 1;

  const masterGain = ctx.createGain();
  masterGain.gain.value = 0.07;

  // FM synthesis
  const fmOsc = ctx.createOscillator();
  fmOsc.type = 'sine';
  fmOsc.frequency.value = 220;
  const fmGain = ctx.createGain();
  fmGain.gain.value = 0; // Off by default, twist enables it

  fmOsc.connect(fmGain).connect(osc.frequency);
  osc.connect(filter).connect(distortion).connect(masterGain).connect(ctx.destination);

  osc.start();
  fmOsc.start();

  return { ctx, osc, filter, distortion, distortionGain, masterGain, fmOsc, fmGain, octaveShift: 0 };
}

export function OrientationPad() {
  const { parser, connected, octaveOffset } = useOddball();
  const cc = useCCValues();
  const engineRef = useRef<PadEngine | null>(null);
  const octaveRef = useRef(0);
  const [pos, setPos] = useState({ x: 0.5, y: 0.5 });

  useEffect(() => { octaveRef.current = octaveOffset; }, [octaveOffset]);
  useEffect(() => {
    const eng = engineRef.current;
    if (eng) eng.masterGain.gain.setTargetAtTime(connected ? 0.07 : 0, eng.ctx.currentTime, 0.05);
  }, [connected]);

  useEffect(() => {
    const eng = createPadEngine();
    engineRef.current = eng;

    const onCC = (evt: CCUpdateEvent) => {
      eng.octaveShift = octaveRef.current;
      const t = eng.ctx.currentTime;
      switch (evt.cc) {
        case OddballCC.OrientX: {
          // 2-octave range: 110-440 Hz, shifted by octave offset
          const freq = 110 * Math.pow(2, (evt.value / 127) * 2 + eng.octaveShift);
          eng.osc.frequency.setTargetAtTime(freq, t, 0.03);
          eng.fmOsc.frequency.setTargetAtTime(freq, t, 0.03);
          setPos((p) => ({ ...p, x: evt.value / 127 }));
          break;
        }
        case OddballCC.OrientY: {
          // Filter cutoff 200-8000 Hz
          const cutoff = 200 * Math.pow(2, (evt.value / 127) * 5.3);
          eng.filter.frequency.setTargetAtTime(Math.min(cutoff, 8000), t, 0.03);
          setPos((p) => ({ ...p, y: evt.value / 127 }));
          break;
        }
        case OddballCC.SpinSmoothed: {
          // Distortion amount
          const amount = (evt.value / 127) * 50;
          eng.distortion.curve = makeDistortionCurve(amount);
          break;
        }
      }
    };

    const onTap = (evt: TapEvent) => {
      // Retrigger envelope
      const t = eng.ctx.currentTime;
      const vol = 0.05 + (evt.velocity / 127) * 0.12;
      eng.masterGain.gain.cancelScheduledValues(t);
      eng.masterGain.gain.setValueAtTime(vol, t);
      eng.masterGain.gain.setTargetAtTime(0.07, t, 0.15);
    };

    const onTwist = (evt: TwistEvent) => {
      // FM depth
      const t = eng.ctx.currentTime;
      const depth = (evt.velocity / 127) * 200;
      eng.fmGain.gain.setTargetAtTime(depth, t, 0.05);
      // Fade out FM after inactivity
      setTimeout(() => {
        eng.fmGain.gain.setTargetAtTime(0, eng.ctx.currentTime, 0.3);
      }, 600);
    };

    parser.on('cc', onCC);
    parser.on('tap', onTap);
    parser.on('twist', onTwist);

    return () => {
      parser.off('cc', onCC);
      parser.off('tap', onTap);
      parser.off('twist', onTwist);
      eng.osc.stop();
      eng.fmOsc.stop();
      eng.masterGain.disconnect();
    };
  }, [parser]);

  const gridSize = 280;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '16px', paddingTop: '20px' }}>
      <h2 style={{ fontSize: '1rem', fontWeight: 300, color: '#888' }}>Orientation Pad</h2>

      {/* XY Grid */}
      <div
        style={{
          position: 'relative',
          width: `${gridSize}px`,
          height: `${gridSize}px`,
          background: '#0a0a0a',
          border: '1px solid #222',
          borderRadius: '8px',
          overflow: 'hidden',
        }}
      >
        {/* Grid lines */}
        <div style={{ position: 'absolute', left: '50%', top: 0, bottom: 0, width: '1px', background: '#1a1a1a' }} />
        <div style={{ position: 'absolute', top: '50%', left: 0, right: 0, height: '1px', background: '#1a1a1a' }} />

        {/* Crosshair dot */}
        <div
          style={{
            position: 'absolute',
            left: `${pos.x * gridSize - 8}px`,
            top: `${(1 - pos.y) * gridSize - 8}px`,
            width: '16px',
            height: '16px',
            borderRadius: '50%',
            background: '#47a',
            boxShadow: '0 0 12px rgba(68, 119, 170, 0.6)',
            transition: 'left 0.05s, top 0.05s',
          }}
        />

        {/* Axis labels */}
        <span style={{ position: 'absolute', bottom: '4px', left: '50%', transform: 'translateX(-50%)', fontSize: '0.6rem', color: '#333' }}>
          Pitch →
        </span>
        <span style={{ position: 'absolute', left: '4px', top: '50%', transform: 'translateY(-50%) rotate(-90deg)', fontSize: '0.6rem', color: '#333' }}>
          Filter →
        </span>
      </div>

      <div style={{ display: 'flex', gap: '16px', fontSize: '0.75rem', color: '#666', fontFamily: 'monospace' }}>
        <span>X: {Math.round(pos.x * 127)}</span>
        <span>Y: {Math.round(pos.y * 127)}</span>
      </div>

      <div style={{ fontSize: '0.7rem', color: '#444', maxWidth: '300px', textAlign: 'center', lineHeight: 1.6 }}>
        Start with the LED pointing upwards for best range.
        <br />
        Orient X = pitch &middot; Orient Y = filter &middot; Spin = distortion &middot; Tap = retrigger &middot; Twist = FM
      </div>
    </div>
  );
}
